import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { SessionActions } from '../../user/auth/auth.actions';
import { Action, createReducer, on } from '@ngrx/store';
import {
  WebsiteBlogCreateActions,
  WebsiteBlogCurrentArticleActions,
  WebsiteBlogListActions,
  deleteWebsiteBlogPostActions,
  updatePostStatusActions,
  updateWebsiteBlogPostActions,
} from './website-blog.actions';
import { BlogPostItem } from '../../../../../models/blog.model';
import { SystemProcess, SystemProcessLoadingStatus } from 'src/app/models/global-classes';

export interface WebsiteBlogState extends EntityState<BlogPostItem> {
  currentPost: BlogPostItem;
  deletePostProcess: {
    status: SystemProcessLoadingStatus;
    error: { processType: SystemProcessLoadingStatus, error: any }
  };
  createPostProcess: {
    status: SystemProcessLoadingStatus;
    error: { processType: SystemProcessLoadingStatus, error: any }
  };
  currentPostProcess: {
    status: SystemProcessLoadingStatus;
    error: { processType: SystemProcessLoadingStatus, error: any }
  };
  postsLoaded: boolean;
  postsError: any;
}

export const blogPostsAdapter = createEntityAdapter<BlogPostItem>();

const initialState: WebsiteBlogState = blogPostsAdapter.getInitialState({
  currentPost: null,
  currentPostProcess: { status: 'none', error: null },
  createPostProcess: { status: 'none', error: null },
  deletePostProcess: { status: 'none', error: null },
  postsLoaded: false,
  postsError: null,
});

export const websiteBlogReducer = createReducer(
  initialState,

  // Posts List
  on(WebsiteBlogListActions.load, (state) => ({
    ...state,
    postsLoaded: false,
    postsError: null,
  })),
  on(WebsiteBlogListActions.loadSuccess, (state, { posts }) =>
    blogPostsAdapter.setAll(posts, {
      ...state,
      postsLoaded: true,
    })
  ),

  on(WebsiteBlogListActions.loadFailure, (state, { error }) => ({
    ...state,
    postsError: error,
  })),

  on(WebsiteBlogCurrentArticleActions.load, (state) => ({
    ...state,
    postsError: null,
    currentPostProcess: { status: 'loading', error: null },
  })),
  on(WebsiteBlogCurrentArticleActions.loadSuccess, (state, { post }) => ({
    ...state,
    currentPost: post,
    currentPostProcess: { status: 'done', error: null },
  })),
  on(WebsiteBlogCurrentArticleActions.loadFailure, (state, { error }) => ({
    ...state,
    postsError: error,
    currentPostProcess: { status: 'error', error: { processType: 'loading', error } },
  })),

  // Post Create
  on(WebsiteBlogCreateActions.create, (state) => ({
    ...state,
    createPostProcess: { status: 'creating', error: null },
  })),
  on(WebsiteBlogCreateActions.loadSuccess, (state, { post }) =>
    blogPostsAdapter.addOne(post, {
      ...state,
      createPostProcess: { status: 'done', error: null },
    })
  ),

  on(WebsiteBlogCreateActions.loadFailure, (state, { error }) => ({
    ...state,
    createPostProcess: { status: 'error', error: { processType: 'creating', error } },
  })),

  on(deleteWebsiteBlogPostActions.delete, (state) => ({
    ...state,
    postsError: null,
    deletePostProcess: { status: 'deleting', error: null },
  })),
  on(deleteWebsiteBlogPostActions.deleteSuccess, (state, { postId }) =>
    blogPostsAdapter.removeOne(postId, {
      ...state,
      deletePostProcess: { status: 'done', error: null },
    })
  ),
  on(
    deleteWebsiteBlogPostActions.deleteFailure,
    (state, { error }) => ({
      ...state,
      postsError: error,
      deletePostProcess: { status: 'error', error: { processType: 'deleting', error } },
    })
  ),

  // Post Update
  on(updateWebsiteBlogPostActions.update, (state) => ({
    ...state,
    currentPostProcess: { status: 'updating', error: null },
  })),
  on(updateWebsiteBlogPostActions.updateSuccess, (state, { post }) =>
  {
    console.log("Updating", post);
    return blogPostsAdapter.updateOne(
      { id: post.id, changes: post },
      {
        ...state,
        currentPost: post?.id == state.currentPost?.id ? {...post} : state.currentPost,
        currentPostProcess: { status: 'done', error: null },
      }
    )
  }
  ),
  on(updateWebsiteBlogPostActions.updateFailure, (state, { error }) => ({
    ...state,
    currentPostProcess: { status: 'error', error: { processType: 'updating', error } },
  })),

  on(updatePostStatusActions.update, (state) => ({
    ...state,
    currentPostProcess: { status: 'updating', error: null },
  })),

  on(SessionActions.forget, (state) => initialState)
);
